import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '@/styles/css/colors';
import type { SimpleLineChartProps } from './types';

/**
 * Minimal dependency-free line chart using an inline SVG polyline.
 * Uses named export to match codebase conventions.
 */
export const SimpleLineChart: React.FC<SimpleLineChartProps> = ({ title, labels, values, height = 200 }) => {
  // Early return for empty data state
  if (!labels.length) {
    return (
      <Box role="region" aria-label={`${title} - No data`}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available.
        </Typography>
      </Box>
    );
  }

  // Memoize chart calculations to prevent recalculation on every render
  const chartData = useMemo(() => {
    const width = Math.max(labels.length * 60, 300);
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const range = max - min || 1;
    const paddingY = 20;

    const points = values.map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * (width - 40) + 20;
      const y = height - paddingY - ((value - min) / range) * (height - paddingY * 2);
      return { x, y, value };
    });

    const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');

    return { width, points, polylinePoints };
  }, [labels.length, values, height]);

  const { width, points, polylinePoints } = chartData;

  return (
    <Box role="region" aria-label={title}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }} component="h3">
        {title}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <svg 
          width={width} 
          height={height + 30} 
          style={{ display: 'block' }}
          role="img"
          aria-label={`Line chart showing ${labels.length} data points`}
        >
          <polyline
            points={polylinePoints}
            fill="none"
            stroke={colors.DASHBOARDGRAPH}
            strokeWidth={2}
            aria-hidden="true"
          />
          {points.map((p, index) => (
            <g key={`${labels[index]}-${p.value}`} aria-label={`${labels[index]}: ${p.value}`}>
              <circle cx={p.x} cy={p.y} r={3} fill={colors.DASHBOARDGRAPH} aria-hidden="true" />
              <text x={p.x} y={p.y - 8} fontSize={10} textAnchor="middle" aria-label={`Value: ${p.value}`}>
                {p.value}
              </text>
              <text
                x={p.x}
                y={height + 15}
                fontSize={9}
                textAnchor="middle"
                aria-label={`Label: ${labels[index]}`}
              >
                {labels[index]}
              </text>
            </g>
          ))}
        </svg>
      </Box>
    </Box>
  );
};
